// src/components/admin/AdminHelpDesk.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { helpDeskService, ROLES } from '../../services/helpDeskService';
import { useAuth } from '../../contexts/AuthContext';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { singleValueListService } from '../../services/singleValueListService';
import AdminHelpDeskModal from '../Modals/AdminHelpDeskModal';

import {
  Box, Container, Typography, Paper, Button, Grid,
  TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton,
  Chip, Alert, InputAdornment, Tooltip, Divider,
  Select, MenuItem, FormControl, InputLabel,
  Snackbar, Skeleton, Fade, Menu, Collapse,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// ─── Local Normalizer ─────────────────────────────────────────────────────────
const normalizeIssue = (raw = {}) => {
  const toDate = (v) => {
    if (!v) return null;
    if (typeof v?.toDate === 'function') return v.toDate();
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };
  const toDisplay = (v) => {
    const d = toDate(v);
    return d ? d.toLocaleString() : null;
  };
  const toMs = (v) => {
    const d = toDate(v);
    return d ? d.getTime() : 0;
  };

  const raisedByRaw = String(raw.raisedBy || 'user').trim().toLowerCase();
  const raisedBy = Object.values(ROLES).includes(raisedByRaw) ? raisedByRaw : ROLES.USER;
  const isAuditorTicket = raisedBy === ROLES.AUDITOR;

  const rawCreatedAt = raw.createdAt ?? raw.metadata?.createdAt ?? raw.created_at ?? null;
  const rawUpdatedAt = raw.updatedAt ?? raw.metadata?.updatedAt ?? raw.updated_at ?? null;
  const rawResolvedAt = raw.resolvedAt ?? raw.metadata?.resolvedAt ?? raw.resolved_at ?? null;

  return {
    ...raw,
    _createdAtRaw: rawCreatedAt,
    _updatedAtRaw: rawUpdatedAt,
    _resolvedAtRaw: rawResolvedAt,
    _createdAtMs: toMs(rawCreatedAt),
    _originalIssueType: raw.issueType || null,
    createdAt: toDisplay(rawCreatedAt) ?? 'N/A',
    updatedAt: toDisplay(rawUpdatedAt) ?? 'N/A',
    resolvedAt: toDisplay(rawResolvedAt) ?? null,
    invoiceFileUrl: raw.maintenanceInvoice?.downloadURL ?? '',
    invoiceFileName: raw.maintenanceInvoice?.fileName ?? '',
    raisedBy,
    issueType: isAuditorTicket ? 'Audit' : (raw.issueType || 'General'),
  };
};

// ─── Pure UI Helpers ──────────────────────────────────────────────────────────
const getColorForPriority = (priority) => {
  const map = {
    Critical: { bg: '#ffebee', text: '#c62828' },
    High:     { bg: '#fff3e0', text: '#ef6c00' },
    Medium:   { bg: '#e3f2fd', text: '#1565c0' },
    Low:      { bg: '#e8f5e9', text: '#2e7d32' },
  };
  return map[priority] || { bg: '#ececec', text: '#595959' };
};

const getColorForStatus = (status) => {
  const map = {
    Open:          { bg: '#e3f2fd', text: '#1565c0' },
    'In Progress': { bg: '#fff9c4', text: '#9a8900' },
    Resolved:      { bg: '#e8f5e9', text: '#2e7d32' },
    Closed:        { bg: '#ececec', text: '#666' },
  };
  return map[status] || { bg: '#ececec', text: '#595959' };
};

const getColorForIssueType = (issueType) => {
  if (issueType === 'Audit') return { bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' };
  return { bg: '#fdf6e4', text: '#bc7800', border: '#edd286' };
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format(amount);

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const HelpDeskSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box><Skeleton variant="text" width={240} height={44} /><Skeleton variant="text" width={380} height={20} /></Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}><Skeleton variant="rounded" width={110} height={40} /><Skeleton variant="rounded" width={100} height={40} /></Box>
    </Box>
    <Box sx={{ p: 2, borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff' }}><Skeleton variant="rounded" width="40%" height={40} /></Box>
    <Box sx={{ borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.5, borderBottom: '2px solid #e0e0e0' }}>
        {[40, 100, 160, 80, 80, 80, 80, 120, 180, 80, 80, 100].map((w, i) => (<Skeleton key={i} variant="text" width={w} height={16} />))}
      </Box>
      {Array.from({ length: 8 }, (_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 2, px: 2, py: 2, borderBottom: '1px solid #f5f5f5', alignItems: 'center' }}>
          <Skeleton variant="text" width={40} height={14} /><Skeleton variant="text" width={100} height={14} /><Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={140} height={14} /><Skeleton variant="rounded" width={70} height={24} />
          <Skeleton variant="rounded" width={80} height={24} /><Skeleton variant="rounded" width={70} height={24} />
          <Skeleton variant="rounded" width={70} height={24} /><Skeleton variant="text" width={100} height={14} />
          <Skeleton variant="text" width={160} height={14} /><Skeleton variant="text" width={60} height={14} />
          <Skeleton variant="text" width={60} height={14} /><Skeleton variant="rounded" width={36} height={36} />
        </Box>
      ))}
    </Box>
  </Container>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminHelpDesk = () => {
  const { currentUser } = useAuth();

  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [statusOptions, setStatusOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [issueTypeOptions, setIssueTypeOptions] = useState([]);

  const [selectedIssue, setSelectedIssue] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTargetIssue, setMenuTargetIssue] = useState(null);

  const [filters, setFilters] = useState({ status: 'All', priority: 'All', issueType: 'All', date: null, search: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [copied, setCopied] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load Metadata
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { companyId } = await helpDeskService.resolveAdminContext();
        const [statuses, priorities, ticketTypes, auditTypes] = await Promise.all([
          singleValueListService.getTicketStatuses({ companyId }),
          singleValueListService.getTicketPriorities({ companyId }),
          singleValueListService.getTicketIssueTypes({ companyId }),
          singleValueListService.getAuditIssueTypes({ companyId }),
        ]);

        if (cancelled) return;
        setStatusOptions(statuses.map(i => String(i.value ?? i.label ?? '')).filter(Boolean));
        setPriorityOptions(priorities.map(i => String(i.value ?? i.label ?? '')).filter(Boolean));
        setIssueTypeOptions(Array.from(new Set([...ticketTypes.map(i => i.value), ...auditTypes.map(i => i.value)])).filter(Boolean));
      } catch (err) { console.error('[AdminHelpDesk] Failed to load SVL options:', err); }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { companyId } = await helpDeskService.resolveAdminContext();
        const techs = await helpDeskService.fetchTechnicians(companyId);
        if (!cancelled) setTechnicians(techs);
      } catch (err) { console.error("[AdminHelpDesk] Failed to load technicians:", err); }
    })();
    return () => { cancelled = true; };
  }, []);

  const showNotification = useCallback((message, type = 'success') => {
    if (type === 'success') { setSuccessMessage(message); setTimeout(() => setSuccessMessage(null), 5000); }
    else { setError(message); setTimeout(() => setError(null), 6000); }
  }, []);

  const fetchIssues = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { companyId } = await helpDeskService.resolveAdminContext();
      const rawIssues = await helpDeskService.fetchIssues(companyId, filters.status);
      const normalized = rawIssues.map(raw => normalizeIssue(raw)).sort((a, b) => b._createdAtMs - a._createdAtMs);
      setIssues(normalized);
    } catch (err) {
      console.error('[AdminHelpDesk] fetchIssues:', err);
      setError({ code: err.code || 'error', message: err.message || 'Failed to load issues.' });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') fetchIssues(true); };
    document.addEventListener('visibilitychange', refresh); window.addEventListener('focus', refresh);
    return () => { document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); };
  }, [fetchIssues]);

  useEffect(() => {
    let result = [...issues];
    if (filters.priority !== 'All') result = result.filter(i => i.priority === filters.priority);
    if (filters.issueType !== 'All') {
      if (filters.issueType === 'Audit') {
        result = result.filter(i => i.raisedBy === ROLES.AUDITOR);
      } else if (filters.issueType === 'Missing Asset') {
        result = result.filter(i => i.raisedBy === ROLES.AUDITOR && i._originalIssueType === 'Missing Asset');
      } else {
        result = result.filter(i => i.issueType === filters.issueType && i.raisedBy !== ROLES.AUDITOR);
      }
    }
    if (filters.date) {
      result = result.filter(i => {
        const raw = i._createdAtRaw;
        const d = typeof raw?.toDate === 'function' ? raw.toDate() : raw instanceof Date ? raw : raw ? new Date(raw) : null;
        return !!d && !isNaN(d) && d >= new Date(filters.date);
      });
    }
    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(i => Object.values(i).some(v => v && typeof v === 'string' && v.toLowerCase().includes(term)));
    }
    setFilteredIssues(result);
    setPage(0);
  }, [issues, filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== 'All') count++; if (filters.priority !== 'All') count++;
    if (filters.issueType !== 'All') count++; if (filters.date) count++;
    return count;
  }, [filters]);

  const openStatusDialog = useCallback((issue) => {
    setSelectedIssue({ ...issue });
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedIssue(null);
  };

  const handleIssueUpdated = (issueId, updateData, newStatus) => {
    setIssues(prev => prev.map(issue => 
      issue.id !== issueId ? issue : normalizeIssue({
        ...issue, ...updateData, updatedAt: new Date(),
        resolvedAt: (newStatus === 'Resolved' || newStatus === 'Closed') ? new Date() : issue._resolvedAtRaw,
      })
    ));
  };

  const handleViewInvoice = useCallback((url, fileName) => {
    try { helpDeskService.openInvoiceFile(url, fileName); }
    catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const handleMenuOpen = (e, issue) => { setAnchorEl(e.currentTarget); setMenuTargetIssue(issue); };
  const handleMenuClose = () => { setAnchorEl(null); setMenuTargetIssue(null); };

  const handleFilterChange = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));
  const resetFilters = () => setFilters({ status: 'All', priority: 'All', issueType: 'All', date: null, search: '' });

  const handleCopyTicketLink = (issueId) => {
    navigator.clipboard.writeText(`${window.location.origin}/helpdesk/ticket/${issueId}`);
    setCopied(true); setTimeout(() => setCopied(false), 1200);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  if (loading) return <HelpDeskSkeleton />;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
        
        {/* HEADER */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}>Help Desk Tickets</Typography>
            <Typography variant="body2" color="text.secondary">Manage, resolve and track all help desk issues across your organisation</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant={showFilters ? 'contained' : 'outlined'} startIcon={<FilterAltIcon />} onClick={() => setShowFilters(v => !v)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, position: 'relative' }}>
              {showFilters ? 'Hide Filters' : 'Filters'}
              {activeFilterCount > 0 && !showFilters && (
                <Box sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'error.main', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {activeFilterCount}
                </Box>
              )}
            </Button>
          </Box>
        </Box>

        {/* SNACKBARS */}
        {copied && <Snackbar open autoHideDuration={600} onClose={() => setCopied(false)}><Alert severity="info" sx={{ fontWeight: 600 }}>Ticket link copied!</Alert></Snackbar>}
        <Snackbar open={!!successMessage} autoHideDuration={5000} onClose={() => setSuccessMessage(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>
        </Snackbar>
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="error" onClose={() => setError(null)}>{error?.message || error}</Alert>
        </Snackbar>

        {/* SEARCH BAR */}
        <Paper sx={{ p: 2, borderRadius: 3, bgcolor: 'background.paper' }} variant="outlined">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" variant="outlined" label="Search Tickets" placeholder="Search by any field..." value={filters.search} onChange={e => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'action.active' }} fontSize="small" /></InputAdornment>,
                  endAdornment: filters.search && <InputAdornment position="end"><IconButton size="small" onClick={() => handleFilterChange('search', '')}><ClearIcon fontSize="small" /></IconButton></InputAdornment>,
                }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Grid>
            {filters.search && <Grid item xs={12} md={8}><Typography variant="body2" color="text.secondary">Found <strong>{filteredIssues.length}</strong> ticket{filteredIssues.length !== 1 ? 's' : ''} matching "{filters.search}"</Typography></Grid>}
          </Grid>
        </Paper>

        {/* FILTERS */}
        <Collapse in={showFilters}>
          <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: 'background.paper' }} variant="outlined">
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={filters.status} label="Status" onChange={e => handleFilterChange('status', e.target.value)} sx={{ borderRadius: 2 }}>
                    <MenuItem value="All">All Statuses</MenuItem>
                    {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select value={filters.priority} label="Priority" onChange={e => handleFilterChange('priority', e.target.value)} sx={{ borderRadius: 2 }}>
                    <MenuItem value="All">All Priorities</MenuItem>
                    {priorityOptions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Issue Type</InputLabel>
                  <Select value={filters.issueType} label="Issue Type" onChange={e => handleFilterChange('issueType', e.target.value)} sx={{ borderRadius: 2 }}>
                    <MenuItem value="All">All Types</MenuItem>
                    <MenuItem value="Audit"><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6a1b9a' }} />Audit (Auditor tickets)</Box></MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                    {issueTypeOptions.filter(t => t !== 'Audit').map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <DatePicker label="From Date" value={filters.date} onChange={v => handleFilterChange('date', v)} slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }} />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {filters.status !== 'All' && <Chip size="small" label={`Status: ${filters.status}`} onDelete={() => handleFilterChange('status', 'All')} color="primary" variant="outlined" />}
                  {filters.priority !== 'All' && <Chip size="small" label={`Priority: ${filters.priority}`} onDelete={() => handleFilterChange('priority', 'All')} color="warning" variant="outlined" />}
                  {filters.issueType !== 'All' && <Chip size="small" label={`Type: ${filters.issueType}`} onDelete={() => handleFilterChange('issueType', 'All')} sx={{ borderColor: '#6a1b9a', color: '#6a1b9a' }} variant="outlined" />}
                  {filters.date && <Chip size="small" label={`From: ${new Date(filters.date).toLocaleDateString()}`} onDelete={() => handleFilterChange('date', null)} variant="outlined" />}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="text" color="info" onClick={resetFilters} startIcon={<ClearIcon fontSize="small" />} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Clear All</Button>
                  <Tooltip title="Close filters" arrow><IconButton size="small" onClick={() => setShowFilters(false)}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* TABLE */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, boxShadow: '0px 4px 16px rgba(0,0,0,0.02)', overflowX: 'auto', '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.12)', borderRadius: 4 } }}>
          {filteredIssues.length === 0 ? (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <Alert severity="info" sx={{ justifyContent: 'center', borderRadius: 2.5 }}>{issues.length === 0 ? 'No tickets found for your company' : 'No tickets match your current filters'}</Alert>
              {issues.length > 0 && <Button onClick={resetFilters} sx={{ mt: 2, textTransform: 'none' }}>Clear Filters</Button>}
            </Box>
          ) : (
                                  <Table sx={{ minWidth: 1500 }} size="medium">
                           <TableHead>
                <TableRow>
                  {[
                    { label: 'Sl.No', align: 'center' }, 
                    { label: 'Ticket ID', align: 'left' }, 
                    { label: 'User', align: 'left' },
                    { label: 'Asset', align: 'left' }, 
                    { label: 'Technician', align: 'left' }, 
                    { label: 'Raised By', align: 'left' }, 
                    { label: 'Issue Type', align: 'left' },
                    { label: 'Priority', align: 'left' }, 
                    { label: 'Status', align: 'left' }, 
                    { label: 'Created', align: 'left' },
                    { label: 'Resolved', align: 'left' }, 
                    { label: 'Resolved By', align: 'left' }, 
                    { label: 'Description', align: 'left' }, 
                    { label: 'Invoice', align: 'left' },
                    { label: 'Cost', align: 'left' }, 
                    { label: 'Actions', align: 'center' },
                  ].map(({ label, align }) => (
                    <TableCell 
                      key={label} 
                      align={align} 
                      sx={{ fontWeight: 700, bgcolor: 'background.neutral', color: 'text.primary', whiteSpace: 'nowrap' }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIssues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                  const priorityColor = getColorForPriority(row.priority || 'Medium');
                  const statusColor = getColorForStatus(row.status || 'Open');
                  const issueTypeColor = getColorForIssueType(row.issueType);
                  const isAuditorTicket = row.raisedBy === ROLES.AUDITOR;

                  return (
                    <TableRow key={row.id} hover sx={{ borderLeft: isAuditorTicket ? '3px solid #6a1b9a' : '3px solid transparent' }}>
                      <TableCell align="center"><Typography variant="body2" fontWeight={600} color="text.secondary">{page * rowsPerPage + index + 1}</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography onClick={() => openStatusDialog(row)} sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'primary.main', fontSize: '0.85rem', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                            #{String(row.id).substring(0, 8)}
                          </Typography>
                          <Tooltip title="Copy Ticket Link" arrow><IconButton size="small" onClick={e => { e.stopPropagation(); handleCopyTicketLink(row.id); }}><ContentCopyIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                                          <TableCell><Typography variant="body2" fontWeight={600} color="text.primary">{String(row.userEmail || 'N/A')}</Typography></TableCell>
                      
                      {/* ── NEW ASSET CELL ── */}
                      <TableCell>
                        {row.assetName || row.assetId ? (
                          <Tooltip title={row.assetId ? `Tag/ID: ${row.assetId}` : ''} arrow>
                            <Box>
                              <Typography variant="body2" fontWeight={600} color="text.primary">
                                {String(row.assetName || 'Unnamed Asset')}
                              </Typography>
                              {row.assetTag && (
                                <Typography variant="caption" color="text.secondary">
                                  {String(row.assetTag)}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip size="small" label={row.assignedToEmail ? 'Assigned' : 'Unassigned'} color={row.assignedToEmail ? 'success' : 'default'} variant="outlined" />
                        {row.assignedToEmail && <Typography variant="caption" display="block" color="text.secondary">{row.assignedToEmail}</Typography>}
                      </TableCell>
                      <TableCell><Chip size="small" label={isAuditorTicket ? 'Auditor' : 'User'} sx={{ fontWeight: 600, fontSize: '0.72rem', bgcolor: isAuditorTicket ? '#f3e5f5' : '#e8f4fd', color: isAuditorTicket ? '#6a1b9a' : '#0d5c8f' }} /></TableCell>
                      <TableCell>
                        <Tooltip title={isAuditorTicket && row._originalIssueType ? `Sub-type: ${row._originalIssueType}` : ''} arrow>
                          <Chip variant="outlined" size="small" label={String(row.issueType || 'General')} sx={{ fontWeight: 500, fontSize: '0.72rem', bgcolor: issueTypeColor.bg, color: issueTypeColor.text, borderColor: issueTypeColor.border }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell><Chip size="small" label={String(row.priority || 'Medium')} sx={{ background: priorityColor.bg, color: priorityColor.text, fontWeight: 700, fontSize: '0.72rem' }} /></TableCell>
                      <TableCell><Chip size="small" label={String(row.status || 'Open')} sx={{ background: statusColor.bg, color: statusColor.text, fontWeight: 700, fontSize: '0.72rem' }} /></TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{String(row.createdAt || 'N/A')}</Typography></TableCell>
                                          <TableCell>{row.resolvedAt ? <Typography variant="body2" color="success.dark" fontWeight={500}>{String(row.resolvedAt)}</Typography> : <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                      
                      {/* ── NEW RESOLVED BY CELL ── */}
                      <TableCell>
                        {row.resolvedBy ? (
                          <Typography variant="body2" color="text.secondary">
                            {String(row.resolvedBy)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={String(row.description || 'No description')} arrow disableHoverListener={!row.description || row.description.length <= 40}>
                          <Typography variant="body2" color="text.secondary" onClick={() => openStatusDialog(row)} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, cursor: 'pointer' }}>
                            {String(row.description || 'No description')}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {row.invoiceFileUrl && row.invoiceFileName ? (
                          <Tooltip title={`View ${row.invoiceFileName}`} arrow>
                            <Button size="small" startIcon={<AttachFileIcon sx={{ fontSize: 13 }} />} onClick={() => handleViewInvoice(row.invoiceFileUrl, row.invoiceFileName)} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.75rem', p: '2px 8px' }}>
                              {row.invoiceFileName.length > 12 ? row.invoiceFileName.slice(0, 12) + '...' : row.invoiceFileName}
                            </Button>
                          </Tooltip>
                        ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2" fontWeight={row.resolutionCost ? 600 : 400} color={row.resolutionCost ? 'text.primary' : 'text.disabled'}>{row.resolutionCost ? formatCurrency(row.resolutionCost) : '—'}</Typography></TableCell>
                      <TableCell align="center"><IconButton size="small" onClick={e => handleMenuOpen(e, row)}><MoreVertIcon fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {filteredIssues.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredIssues.length)} of {filteredIssues.length} tickets</Typography>
            <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredIssues.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ border: 'none', overflow: 'hidden' }} />
          </Box>
        )}

        {/* CONTEXT MENU */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} TransitionComponent={Fade} PaperProps={{ sx: { minWidth: 220, borderRadius: 2.5, boxShadow: '0px 4px 20px rgba(0,0,0,0.08)' } }} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={() => { openStatusDialog(menuTargetIssue); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}><EditIcon fontSize="small" color="action" /><Typography variant="body2" fontWeight={500}>View / Update</Typography></MenuItem>
          <MenuItem onClick={() => { handleCopyTicketLink(menuTargetIssue?.id); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}><ContentCopyIcon fontSize="small" color="action" /><Typography variant="body2" fontWeight={500}>Copy Ticket Link</Typography></MenuItem>
          {menuTargetIssue?.invoiceFileUrl && <MenuItem onClick={() => { handleViewInvoice(menuTargetIssue.invoiceFileUrl, menuTargetIssue.invoiceFileName); handleMenuClose(); }} sx={{ gap: 1.5, py: 1 }}><AttachFileIcon fontSize="small" color="action" /><Typography variant="body2" fontWeight={500}>View Invoice</Typography></MenuItem>}
          <Divider sx={{ my: 0.5 }} />
         {['Resolved', 'Closed']
  .filter(s => statusOptions.includes(s))
  .filter(() => {
    // Only the current assignee may Resolve/Close.
    const actorEmail = String(currentUser?.email || '').trim().toLowerCase();
    const assignee   = String(menuTargetIssue?.assignedToEmail || '').trim().toLowerCase();
    return assignee && actorEmail && actorEmail === assignee;
  })
  .map(status => (
    <MenuItem key={status} onClick={() => { openStatusDialog({ ...menuTargetIssue, status }); handleMenuClose(); }} sx={{ gap: 1.5, py: 1, color: 'success.main' }}>
      <CheckCircleIcon fontSize="small" color="success" /><Typography variant="body2" fontWeight={500}>Mark as {status}</Typography>
    </MenuItem>
  ))}
        </Menu>

        {/* ── SEPARATED FULL-SCREEN DIALOG COMPONENT ── */}
        <AdminHelpDeskModal
          open={dialogOpen}
          onClose={handleCloseDialog}
          issue={selectedIssue}
          setIssue={setSelectedIssue}
          onUpdate={handleIssueUpdated}
          technicians={technicians}
          currentUser={currentUser}
          showNotification={showNotification}
        />

      </Container>
    </LocalizationProvider>
  );
};

export default AdminHelpDesk;